"use client";

import { Clock, CheckCircle, AlertCircle, Info, XCircle, FileText, FileOutput } from "lucide-react";

interface Activity {
  id: string;
  type: "success" | "warning" | "error" | "info";
  title: string;
  description: string;
  time: string;
  /** Lien de téléchargement Word (.docx). */
  downloadDocxUrl?: string;
  /** Lien de téléchargement PDF. */
  downloadPdfUrl?: string;
}

interface ActivityFeedProps {
  activities: Activity[];
}

const activityIcons = {
  success: CheckCircle,
  warning: AlertCircle,
  error: XCircle,
  info: Info,
};

const activityColors = {
  success: "text-success-600 bg-success-50",
  warning: "text-warning-600 bg-warning-50",
  error: "text-error-600 bg-error-50",
  info: "text-info-600 bg-info-50",
};

export function ActivityFeed({ activities }: ActivityFeedProps) {
  if (activities.length === 0) {
    return (
      <div className="card p-8 text-center animate-fade-in">
        <Clock className="h-12 w-12 text-slate-300 mx-auto mb-3" />
        <p className="text-sm font-semibold text-slate-500">Aucune activité récente</p>
      </div>
    );
  }

  return (
    <div className="card p-6 animate-fade-in">
      <h3 className="text-lg font-bold text-slate-900 mb-4">Activité récente</h3>
      <div className="space-y-3">
        {activities.map((activity) => {
          const Icon = activityIcons[activity.type] ?? Info;
          const hasDownloads = activity.downloadDocxUrl || activity.downloadPdfUrl;
          return (
            <div
              key={activity.id}
              className="flex items-start gap-4 p-4 rounded-2xl bg-white/60 backdrop-blur-xl border border-white/50 hover:shadow-md transition-all duration-300"
            >
              <div
                className={`flex h-11 w-11 items-center justify-center rounded-2xl ${activityColors[activity.type] ?? activityColors.info} shadow-sm flex-shrink-0`}
              >
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-900">{activity.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{activity.description}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {hasDownloads && (
                  <>
                    {activity.downloadDocxUrl && (
                      <a
                        href={activity.downloadDocxUrl}
                        className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-primary-600 transition-colors"
                        title="Télécharger en Word"
                      >
                        <FileText className="h-4 w-4" />
                      </a>
                    )}
                    {activity.downloadPdfUrl && (
                      <a
                        href={activity.downloadPdfUrl}
                        className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-primary-600 transition-colors"
                        title="Télécharger en PDF"
                      >
                        <FileOutput className="h-4 w-4" />
                      </a>
                    )}
                  </>
                )}
                <span className="text-xs font-medium text-slate-400 whitespace-nowrap ml-1">
                  {activity.time}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
